import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { formatNumber } from "utils/utils";
import { formatTransactionDate } from "../logic/tips.format.logic";
import { TipsTransactionsLoader } from "./tips.transactionsLoader.component";
import {
  getReferenceLabel,
  getStatusIconColor,
  getStatusIconName,
  getStatusLabel,
  toTransactionKey,
} from "./tips.transactions.logic";
import {
  TransactionAmount,
  TransactionLoadMoreTrigger,
  TransactionPlaceholderAvatar,
  TransactionStatusPill,
  TransactionTable,
  TransactionTableCell,
  TransactionTableHead,
  TransactionTableRow,
  TransactionWorker,
} from "./tips.transactions.styled";
import type { TransactionItem } from "./tips.transactions.types";

interface TipsTransactionRowProps {
  readonly locale: string;
  readonly transaction: TransactionItem;
}

interface TipsTransactionsTableProps {
  readonly hasMoreTransactions: boolean;
  readonly isLoadingMore: boolean;
  readonly loadMoreTriggerRef: RefObject<HTMLDivElement>;
  readonly transactions: readonly TransactionItem[];
}

const TipsTransactionRow = (props: TipsTransactionRowProps): JSX.Element => {
  const { locale, transaction } = props;
  const { t } = useTranslation();

  return (
    <TransactionTableRow key={toTransactionKey(transaction)}>
      <TransactionTableCell>
        {formatTransactionDate({
          dateValue: transaction.createdAt,
          locale,
        })}
      </TransactionTableCell>
      <TransactionTableCell>
        <TransactionWorker>
          <TransactionPlaceholderAvatar>?</TransactionPlaceholderAvatar>
          {transaction.type === "withdrawal"
            ? t("Tips.TransactionWorkerPlaceholder")
            : "Simflexity"}
        </TransactionWorker>
      </TransactionTableCell>
      <TransactionTableCell>
        <TransactionAmount>
          {transaction.type === "withdrawal" ? "- " : "+ "}
          {formatNumber(transaction.amount)} {t("Tips.Currency")}
        </TransactionAmount>
      </TransactionTableCell>
      <TransactionTableCell>
        {transaction.type === "withdrawal"
          ? t("Tips.TransactionPayout")
          : t("TipCredit.Deposit")}
      </TransactionTableCell>
      <TransactionTableCell>
        {transaction.type === "withdrawal" ? t("Tips.Swish") : "-"}
      </TransactionTableCell>
      <TransactionTableCell>
        <TransactionStatusPill $status={transaction.status}>
          <CustomIcon
            name={getStatusIconName(transaction.status)}
            size="18px"
            color={getStatusIconColor(transaction.status)}
          />
          {getStatusLabel({
            status: transaction.status,
            t,
          })}
        </TransactionStatusPill>
      </TransactionTableCell>
      <TransactionTableCell>
        {getReferenceLabel({
          transaction,
        })}
      </TransactionTableCell>
    </TransactionTableRow>
  );
};

export const TipsTransactionsTable = (
  props: TipsTransactionsTableProps,
): JSX.Element => {
  const {
    hasMoreTransactions,
    isLoadingMore,
    loadMoreTriggerRef,
    transactions,
  } = props;
  const { i18n, t } = useTranslation();

  return (
    <TransactionTable>
      <TransactionTableHead>
        <TransactionTableCell>{t("TipCredit.Date")}</TransactionTableCell>
        <TransactionTableCell>{t("Tips.Worker")}</TransactionTableCell>
        <TransactionTableCell>{t("TipCredit.Amount")}</TransactionTableCell>
        <TransactionTableCell>{t("TipCredit.Type")}</TransactionTableCell>
        <TransactionTableCell>{t("Tips.Method")}</TransactionTableCell>
        <TransactionTableCell>{t("TipCredit.Status")}</TransactionTableCell>
        <TransactionTableCell>{t("TipCredit.Reference")}</TransactionTableCell>
      </TransactionTableHead>

      {transactions.map((transaction) => (
        <TipsTransactionRow
          key={toTransactionKey(transaction)}
          locale={i18n.language}
          transaction={transaction}
        />
      ))}
      {hasMoreTransactions && (
        <TransactionLoadMoreTrigger ref={loadMoreTriggerRef}>
          {isLoadingMore && <TipsTransactionsLoader size={24} />}
        </TransactionLoadMoreTrigger>
      )}
    </TransactionTable>
  );
};
